import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RegisteredRetailer } from 'src/app/models/registered-retailer.model';
import { RetailerDataStoreService } from 'src/app/service/retailer-data-store.service';
import { RetailerService } from 'src/app/service/retailer.service';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { NetworkService } from 'src/app/service/network.service';
@Component({
  selector: 'app-capture-photo',
  templateUrl: './capture-photo.component.html',
  styleUrls: ['./capture-photo.component.scss']
})
export class CapturePhotoComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef;

  currentRetailer: RegisteredRetailer | null = null;
  isEditFlow: boolean = false;
  capturedImageFile: File | null = null;
  capturedImageUrl: string | ArrayBuffer | null = null;
  isLoading: boolean = false;
  isLocating: boolean = false;

  latitude: number | null = null;
  longitude: number | null = null;
  locationAccuracy: number | null = null;
  locationError: string | null = null;

  state: string | null = null;
  district: string | null = null;
  village: string | null = null;
  isGeocoding: boolean = false;

  private watchId: number | null = null;
  private networkStatusSubscription: Subscription | undefined;
  isOnline: boolean = true;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private retailerService: RetailerService,
    private retailerDataStore: RetailerDataStoreService,
    private location: Location,
    private networkService: NetworkService
  ) { }

  ngOnInit(): void {
    this.networkStatusSubscription = this.networkService.networkStatus$.subscribe(isOnline => {
      this.isOnline = isOnline;
      if(isOnline && ((!this.state || !this.district || !this.village))){
        this.getCurrentLocation();
      }
    });
    if(this.retailerDataStore.getNavigateToLandingPage()) {
      this.retailerDataStore.clearCapturedImageFile();
      this.clearCapturedImage();
      this.retailerDataStore.setNavigateToLandingPage(false);
      console.log('Navigating to landing page');
      this.router.navigate(['/dealers']);
    }
    this.route.queryParams.subscribe(params => {
      this.isEditFlow = params['isEditFlow'] === 'true';

      // Get the current retailer object from the store
      this.currentRetailer = this.retailerDataStore.getRetailer();
      this.retailerDataStore.clearCapturedImageFile();
      console.log('Current retailer:', this.currentRetailer);
      const primaryMobileFromParams = params['primaryMobile'];
      if (!this.retailerDataStore.getRetailer() || (primaryMobileFromParams && primaryMobileFromParams !== this.retailerDataStore.getRetailer()?.mobileNumberPrimary)) {
        console.log('Primary mobile from params:', primaryMobileFromParams);
        if (primaryMobileFromParams) {
          this.currentRetailer = {
            mobileNumberPrimary: primaryMobileFromParams,
            retailerShopImage: '',
            retailerId: '',
            retailerName: '',
            shopName: '',
            district: '',
            state: '',
            village: '',
            latitude: 0,
            longitude: 0,
            type: 'MAIN'
          };
          this.retailerDataStore.setRetailer(this.currentRetailer);
          console.log('Current retailer set:', this.currentRetailer);
        } else {
          this.snackBar.open('Retailer data missing. Please start from the beginning.', 'Dismiss', { duration: 5000 });
          this.router.navigate(['/dealers']);
          return;
        }
      }

      // If it's an edit flow, pre-populate existing image/location if present
      if (this.isEditFlow && this.currentRetailer) {
          this.latitude = this.currentRetailer.latitude || null;
          this.longitude = this.currentRetailer.longitude || null;
          this.state = this.currentRetailer.state || null;
          this.district = this.currentRetailer.district || null;
          this.village = this.currentRetailer.village || null;
      }
    });

    this.getCurrentLocation();
  }

  ngOnDestroy(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
    }
    this.networkStatusSubscription?.unsubscribe();
  }

  goBack(): void {
    this.location.back();
    this.location.back();
  }

  openCamera(): void {
    if (this.latitude === null || this.longitude === null) {
      this.snackBar.open('Location coordinates are not available. Please wait or ensure location permissions are granted.', 'Dismiss', { duration: 5000 });
      this.getCurrentLocation();
      return;
    }
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      if (!file.type.startsWith('image/')) {
        this.snackBar.open('Please select an image file.', 'Dismiss', { duration: 3000 });
        this.capturedImageFile = null;
        this.capturedImageUrl = null;
        return;
      }

      // Check file size initially to give early feedback for extremely large files (optional)
      const MAX_ORIGINAL_SIZE_MB = 10; // e.g., don't even process if > 10MB
      if (file.size > MAX_ORIGINAL_SIZE_MB * 1024 * 1024) {
        this.snackBar.open(`Image is too large (>${MAX_ORIGINAL_SIZE_MB}MB). Please select a smaller one.`, 'Dismiss', { duration: 5000 });
        this.capturedImageFile = null;
        this.capturedImageUrl = null;
        input.value = ''; // Clear the input
        return;
      }

      const reader = new FileReader();
      reader.onerror = (readerEvent) => {
        console.error('FileReader error:', reader.error); // Log the error object
        this.snackBar.open('Error reading image file.' + reader.error, 'Dismiss', { duration: 5000 });
        this.retailerDataStore.clearCapturedImageFile(); // Clear any partial state
        input.value = ''; // Clear the input
      };      
      reader.onload = (e) => {
        const img = new Image();
        img.onerror = (imgErrorEvent) => {
          console.error('Image loading error:', imgErrorEvent);
          this.snackBar.open('Error loading image for processing. It might be corrupted.' + imgErrorEvent.toString(), 'Dismiss', { duration: 5000 });
          this.retailerDataStore.clearCapturedImageFile(); // Clear any partial state
          input.value = ''; // Clear the input
        };
        img.onload = () => {
          // Now resize and compress the image
          const resizedDataUrl = this.resizeImage(img);

          // Convert Data URL back to a File object
          const resizedFile = this.dataURLtoFile(resizedDataUrl, file.name);

          console.log('Original file size:', (file.size / 1024 / 1024).toFixed(2), 'MB');
          console.log('Resized file size:', (resizedFile.size / 1024 / 1024).toFixed(2), 'MB');

          this.capturedImageFile = resizedFile; // Store the resized file
          this.retailerDataStore.setCapturedImageFile(resizedFile); // Store in data store

          this.capturedImageUrl = resizedDataUrl; // Display the resized image
          input.value = ''; // Clear the input to allow selecting the same file again
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  private resizeImage(img: HTMLImageElement): string {
    const MAX_WIDTH = 800; // Desired max width for the image
    const MAX_HEIGHT = 600; // Desired max height for the image
    const JPEG_QUALITY = 0.8; // JPEG quality (0.0 to 1.0)

    let width = img.width;
    let height = img.height;

    // Calculate new dimensions to fit within MAX_WIDTH x MAX_HEIGHT while maintaining aspect ratio
    if (width > height) {
      if (width > MAX_WIDTH) {
        height *= MAX_WIDTH / width;
        width = MAX_WIDTH;
      }
    } else {
      if (height > MAX_HEIGHT) {
        width *= MAX_HEIGHT / height;
        height = MAX_HEIGHT;
      }
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(img, 0, 0, width, height);
    }

    // Convert canvas content to a data URL (JPEG format for better compression)
    return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  }

  // Helper function to convert Data URL to File object
  private dataURLtoFile(dataurl: string, filename: string): File {
    const arr = dataurl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg'; // Default to jpeg if mime not found
    const bstr = atob(arr[arr.length - 1]); // Base64 part
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  getCurrentLocation(): void {
    if (!navigator.geolocation) {
      this.locationError = 'Geolocation is not supported by your browser.';
      this.snackBar.open(this.locationError, 'Dismiss', { duration: 5000 });
      return;
    }

    this.isLocating = true;
    this.locationError = null;
    this.latitude = null;
    this.longitude = null;
    this.locationAccuracy = null;
    this.state = null;
    this.district = null;
    this.village = null;
    this.isGeocoding = false;

    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        this.latitude = position.coords.latitude;
        this.longitude = position.coords.longitude;
        this.locationAccuracy = position.coords.accuracy;
        this.isLocating = false;

        if (position.coords.accuracy < 30) { 
            if (this.watchId !== null) {
                navigator.geolocation.clearWatch(this.watchId);
                this.watchId = null;
            }
            this.getAdministrativeDetails(this.latitude, this.longitude);
        }
      },
      (error) => {
        this.isLocating = false;
        this.latitude = null;
        this.longitude = null;
        this.locationAccuracy = null;
        switch (error.code) {
          case error.PERMISSION_DENIED:
            this.locationError = 'Location permission denied by user. Cannot capture location.';
            this.snackBar.open('Location permission denied. Please enable it in your browser settings.', 'Dismiss', { duration: 7000 });
            break;
          case error.POSITION_UNAVAILABLE:
            this.locationError = 'Location information is unavailable. Check your device settings.';
            this.snackBar.open('Location information is unavailable. Check your device settings.', 'Dismiss', { duration: 5000 });
            break;
          case error.TIMEOUT:
            this.locationError = 'The request to get user location timed out. Please try again.';
            this.snackBar.open('Failed to get location. Please check if location is enabled.', 'Dismiss', { duration: 5000 });
            break;
          default:
            this.locationError = 'An unknown error occurred while getting location.';
            this.snackBar.open('An unknown error occurred while getting location.', 'Dismiss', { duration: 5000 });
            break;
        }
        console.error('Geolocation Error:', error);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  }

  getAdministrativeDetails(lat: number, lng: number): void {
    this.isGeocoding = true;

    this.retailerService.getFieldOfficerLocation(lat, lng).subscribe(
      response => {
        this.isGeocoding = false;
        if (response && response.data) {
          const locationData = response.data;
          this.state = locationData.state;
          this.district = locationData.district;
          this.village = locationData.village;
          this.snackBar.open('Location details retrieved!', 'Dismiss', { duration: 2000 });
          console.log('Parsed Location Details:', locationData);
        } else {
          this.snackBar.open('Could not retrieve detailed location information.', 'Dismiss', { duration: 5000 });
          console.warn('Location API response not OK or no results:', response);
          this.locationError = 'Could not get detailed address.';
        }
      },
      error => {
        this.isGeocoding = false;
        this.snackBar.open('Error getting location details from Location API. Please check your internet connection and API key.', 'Dismiss', { duration: 7000 });
        this.locationError = 'Failed to get address details.';
        console.error('Location API Error:', error);
      }
    );
  }

  clearCapturedImage(): void {
    this.capturedImageFile = null;
    this.capturedImageUrl = null;
    this.retailerDataStore.clearCapturedImageFile(); // Clear from data store as well
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = ''; // Clear the input element's value
    }
    this.snackBar.open('Image removed successfully.', 'Dismiss', { duration: 2000 });
  }

  saveAndNext(): void {
    if (!this.capturedImageFile && !this.capturedImageUrl) {
      this.snackBar.open('Please capture a photo of the shop.', 'Dismiss', { duration: 3000 });
      return;
    }
    if (this.latitude === null || this.longitude === null) {
      this.getCurrentLocation();
      this.snackBar.open('Location coordinates are not available. Please wait or ensure location permissions are granted.', 'Dismiss', { duration: 5000 });
      return;
    }
    if (!this.state && !this.district && !this.village) {
      this.snackBar.open('Administrative details (county, sub-county, ward) are still loading or could not be retrieved. Please wait.', 'Dismiss', { duration: 5000 });
      this.getCurrentLocation();
      return;
    }

    this.isLoading = true;

    if (this.currentRetailer) {
        this.currentRetailer.latitude = this.latitude;
        this.currentRetailer.longitude = this.longitude;
        this.currentRetailer.state = this.state || 'UNKNOWN';
        this.currentRetailer.district = this.district || 'UNKNOWN';
        this.currentRetailer.village = this.village || 'UNKNOWN';
        // Set name/ownerName as placeholder if not already set (for new flows)
        if (!this.currentRetailer.retailerName) this.currentRetailer.retailerName = '';
        if (!this.currentRetailer.shopName) this.currentRetailer.shopName = '';
        if (!this.currentRetailer.type) this.currentRetailer.type = 'MAIN';
        if (!this.currentRetailer.mobileNumberPrimary) this.currentRetailer.mobileNumberPrimary = parseInt(this.currentRetailer.mobileNumberPrimary.toString(), 9);
    }

    // Update the retailer in the shared store
    this.retailerDataStore.setRetailer(this.currentRetailer);
    this.router.navigate(['/dealer-details-new'], {
      queryParams: {
        isEditFlow: this.isEditFlow
      }
    });
    this.isLoading = false;
  }
}